import React from "react";
import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { MobileSelectSheet, SelectOption } from "./MobileSelectSheet";

type CommonProps = {
  options: SelectOption[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  renderValue?: (opt: SelectOption | null) => React.ReactNode;
  onSelect?: (opt: SelectOption) => void;
};

// Dành cho RHF mode
type RHFProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  rules?: any;
};

// Dành cho controlled mode (không RHF)
type ControlledProps = {
  value: string | number | null;
  onChange: (val: string | number | null) => void;
  errorText?: string | null;
};

type Props<T extends FieldValues> =
  | (CommonProps & RHFProps<T>)
  | (CommonProps & ControlledProps);

function isRHFProps<T extends FieldValues>(
  p: Props<T>
): p is CommonProps & RHFProps<T> {
  return (p as any).control && (p as any).name;
}

export function RHFMobileSelectSheet<T extends FieldValues>(props: Props<T>) {
  const {
    options,
    label = "Chọn",
    placeholder = "Chọn...",
    disabled,
    renderValue,
    onSelect,
  } = props;

  if (isRHFProps(props)) {
    const { control, name, rules } = props;
    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field, fieldState }) => {
          const valueNum =
            typeof field.value === "string"
              ? isNaN(Number(field.value))
                ? field.value
                : Number(field.value)
              : field.value ?? null;

          const selected = options.find((o) => o.value === valueNum) ?? null;
          return (
            <div style={{ width: "100%" }}>
              <MobileSelectSheet
                options={options}
                title={label}
                selectedValue={selected?.value ?? null}
                onSelect={(opt) => {
                  field.onChange(opt.value);
                  onSelect?.(opt);
                }}
                renderTrigger={(open) => (
                  <button
                    type="button"
                    onClick={open}
                    disabled={disabled}
                    className="mobile-input-base"
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "11.5px 14px",
                      background: disabled ? "#f9fafb" : "#fff",
                      color: selected ? "#111827" : "#6b7280",
                      fontSize: "14px"
                    }}
                  >
                    <span className="text-left">
                      {renderValue
                        ? renderValue(selected)
                        : selected?.label ?? placeholder}
                    </span>
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                      <path
                        d="M7 10l5 5 5-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                )}
              />
              {fieldState.error && (
                <div style={{ color: "#ef4444", fontSize: 14, marginTop: 6 }}>
                  <span>{fieldState.error.message?.toString()}</span>
                </div>
              )}
            </div>
          );
        }}
      />
    );
  }

  // Controlled mode (không RHF)
  const { value, onChange, errorText } = props;
  const selected = options.find((o) => o.value === value) ?? null;

  return (
    <div style={{ width: "100%" }}>
      <MobileSelectSheet
        options={options}
        title={label}
        selectedValue={selected?.value ?? null}
        onSelect={(opt) => {
          onChange(opt.value);
          onSelect?.(opt);
        }}
        renderTrigger={(open) => (
          <button
            type="button"
            onClick={open}
            disabled={disabled}
            className="mobile-input-base"
            style={{
              border: selected ? "2px solid #22c55e" : "",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "11px 14px",
              background: disabled ? "#f9fafb" : "#fff",
              color: selected ? "#111827" : "#6b7280",
            }}
          >
            <span className="text-left line-clamp-1 no-wrap">
              {renderValue
                ? renderValue(selected)
                : selected?.label ?? placeholder}
            </span>
            {/* <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg> */}
          </button>
        )}
      />
      {errorText && (
        <div style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>
          {errorText}
        </div>
      )}
    </div>
  );
}
